if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.sliderEnabled = this.dataset.sliderEnabled === "true";
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnailSlider: this.querySelector('[id^="GalleryThumbnails"]'),
          swiperMain: this.querySelector(".product-swiper__main"),
          swiperThumbs: this.querySelector(".product-swiper__thumbs"),
        };
        this.elements.thumbnails = this.sliderEnabled
          ? this.elements.swiperThumbs
          : this.elements.thumbnailSlider;

        this.mql = window.matchMedia("(min-width: 750px)");

        if (this.sliderEnabled) {
          this.initSwiperGallery();
          return;
        }

        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      initSwiperGallery() {
        if (!this.elements.viewer) return;

        this.carouselPrev = this.elements.viewer.querySelector(
          '[data-swiper-control="prev"]'
        );
        this.carouselNext = this.elements.viewer.querySelector(
          '[data-swiper-control="next"]'
        );

        this.bindCarouselThumbClicks();

        if (typeof window.Swiper === "undefined") {
          this.bindCarouselFallback();
          const initialSlide =
            this.elements.viewer.querySelector(
              ".product-swiper__slide.is-active"
            ) || this.elements.viewer.querySelector(".product-swiper__slide");
          if (initialSlide) {
            this.updateSwiperState(
              initialSlide.dataset.mediaId,
              initialSlide.dataset.mediaPosition
            );
          }
          return;
        }

        if (this.elements.swiperThumbs) {
          this.swiperThumbsInstance = new window.Swiper(
            this.elements.swiperThumbs,
            {
              slidesPerView: "auto",
              spaceBetween: 8,
              watchSlidesProgress: true,
              watchSlidesVisibility: true,
              slideToClickedSlide: false,
              freeMode: true,
            }
          );
        }

        if (this.elements.swiperMain) {
          this.swiperMainInstance = new window.Swiper(
            this.elements.swiperMain,
            {
              slidesPerView: 1,
              spaceBetween: 0,
              watchSlidesProgress: true,
              autoHeight: true,
              navigation: {
                nextEl: this.carouselNext,
                prevEl: this.carouselPrev,
              },
              on: {
                slideChange: () => {
                  this.handleSwiperSlideChange();
                },
              },
            }
          );
        }

        this.handleSwiperSlideChange();
      }

      refreshCarousel() {
        if (!this.sliderEnabled || !this.elements.viewer) return;

        this.elements.swiperMain = this.querySelector(".product-swiper__main");
        this.elements.swiperThumbs = this.querySelector(
          ".product-swiper__thumbs"
        );
        this.elements.thumbnails = this.elements.swiperThumbs;

        if (this.swiperThumbsInstance) {
          this.swiperThumbsInstance.update();
        }

        if (this.swiperMainInstance) {
          this.swiperMainInstance.update();
        }

        this.bindCarouselThumbClicks();
        if (this.swiperMainInstance) {
          this.handleSwiperSlideChange();
        } else {
          const initialSlide =
            this.elements.viewer.querySelector(
              ".product-swiper__slide.is-active"
            ) || this.elements.viewer.querySelector(".product-swiper__slide");
          if (initialSlide) {
            this.updateSwiperState(
              initialSlide.dataset.mediaId,
              initialSlide.dataset.mediaPosition
            );
          }
        }
      }

      bindCarouselThumbClicks() {
        this.elements.viewer
          ?.querySelectorAll(".product-swiper__thumb-button")
          .forEach((button) => {
            button.addEventListener("click", (event) => {
              event.preventDefault();
              const target = event.currentTarget.dataset.mediaTarget;
              this.setActiveMedia(target, false);
            });
          });
      }

      bindCarouselFallback() {
        if (this.carouselPrev) {
          this.carouselPrev.addEventListener("click", () =>
            this.moveFallbackBy(-1)
          );
        }
        if (this.carouselNext) {
          this.carouselNext.addEventListener("click", () =>
            this.moveFallbackBy(1)
          );
        }
      }

      moveFallbackBy(delta) {
        const slides = this.getCarouselSlides();
        if (!slides.length) return;

        let currentIndex = slides.findIndex((slide) =>
          slide.classList.contains("is-active")
        );
        if (currentIndex < 0) currentIndex = 0;

        let nextIndex = currentIndex + delta;
        if (nextIndex < 0) nextIndex = slides.length - 1;
        if (nextIndex >= slides.length) nextIndex = 0;

        const nextSlide = slides[nextIndex];
        this.updateSwiperState(
          nextSlide.dataset.mediaId,
          nextSlide.dataset.mediaPosition
        );
      }

      handleSwiperSlideChange() {
        if (!this.swiperMainInstance) return;

        const activeIndex = this.swiperMainInstance.activeIndex;
        const slides = this.swiperMainInstance.slides || [];
        const activeSlide = slides[activeIndex];
        if (!activeSlide) return;

        this.updateSwiperState(
          activeSlide.dataset.mediaId,
          activeSlide.dataset.mediaPosition
        );
      }

      getCarouselSlides() {
        return Array.from(
          this.elements.viewer?.querySelectorAll(".product-swiper__slide") || []
        );
      }

      updateSwiperState(mediaId, position) {
        if (!mediaId) return;

        const slides = this.getCarouselSlides();
        const activeSlide = slides.find(
          (slide) => slide.dataset.mediaId === mediaId
        );
        if (!activeSlide) return;

        slides.forEach((slide) => {
          slide.classList.toggle("is-active", slide === activeSlide);
        });

        this.preventStickyHeader();
        this.playActiveMedia(activeSlide);

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        const activeButton = activeThumbnail?.querySelector("button");
        if (activeButton) activeButton.setAttribute("aria-current", true);

        if (this.swiperThumbsInstance && activeThumbnail) {
          const thumbIndex = Array.from(
            this.swiperThumbsInstance.slides || []
          ).findIndex(
            (slide) => slide.dataset && slide.dataset.target === mediaId
          );
          if (thumbIndex >= 0) this.swiperThumbsInstance.slideTo(thumbIndex);
        }

        if (position) {
          this.announceLiveRegion(activeSlide, position);
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (this.sliderEnabled) {
          this.setCarouselActiveMedia(mediaId);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setCarouselActiveMedia(mediaId) {
        if (!mediaId) return;

        const targetSlide = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        if (!targetSlide) return;

        const position = targetSlide.dataset.mediaPosition;

        if (this.swiperMainInstance) {
          const slideIndex = Array.from(
            this.swiperMainInstance.slides || []
          ).findIndex((slide) => slide.dataset.mediaId === mediaId);
          if (slideIndex >= 0) {
            this.swiperMainInstance.slideTo(slideIndex);
          }
        }

        this.updateSwiperState(mediaId, position);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);

        if (this.sliderEnabled) return;
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;

        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
